import { useState, useEffect, useRef, useMemo, RefObject } from 'react';
import { Outlet, useLocation, useOutletContext } from 'react-router-dom';
import queryString from 'query-string';
import {
  IconLoader,
  Filters,
  Table,
  useFilters,
  useQueryState,
  useScroll,
  usePrevious,
  useGetList,
  SchemaFilterType,
  SelectItemProps,
  ButtonSecondary,
  IconDownload,
  ButtonSize,
  PermissionEnum,
  formatBalance,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import {
  columns,
  columnsLabels,
  schema,
  filtersSchema,
} from './variables/customers';
import { setNotificationVisible } from '../../../../../store/slice';
import {
  useLazyGetOrganizationsWarningsReportQuery,
  useExportWarningReportMutation,
} from './store/service';
import WarningsReportFilters from '../../components/warnings-report/filters';
import useGetAutocompleteList from './hooks/use-get-organizations';

import cn from './warning-report.module.css';

const formatCustomersWarnings = (list: any[]): any[] => {
  const nextList = list?.map((item) => {
    const result = { ...item };

    const render: Record<string, React.JSX.Element> = {
      name: <div className={cn.warningReportCustomerName}>{result.name}</div>,
      percentOfTotal: (
        <div className={cn.warningReportTableRow}>
          {result.percentOfTotal > 0 ? `${result.percentOfTotal}%` : '0%'}
        </div>
      ),
      totalWarnings: (
        <div className={cn.warningReportTableRow}>
          {formatBalance(result.totalWarnings || 0, undefined, 0)}
        </div>
      ),
      customersBlockListOngoingMonitoring: (
        <div className={cn.warningReportTableRow}>
          {formatBalance(
            result.customersBlockListOngoingMonitoring || 0,
            undefined,
            0,
          )}
        </div>
      ),
      transactionsBlockListsMonitoring: (
        <div className={cn.warningReportTableRow}>
          {formatBalance(
            result.transactionsBlockListsMonitoring || 0,
            undefined,
            0,
          )}
        </div>
      ),
      transactionsRiskScoringMonitoring: (
        <div className={cn.warningReportTableRow}>
          {formatBalance(
            result.transactionsRiskScoringMonitoring || 0,
            undefined,
            0,
          )}
        </div>
      ),
      transactionsMonitoringRules: (
        <div className={cn.warningReportTableRow}>
          {formatBalance(result.transactionsMonitoringRules || 0, undefined, 0)}
        </div>
      ),
      paymentsOrdersBlocklistsMonitoring: (
        <div className={cn.warningReportTableRow}>
          {formatBalance(
            result.paymentsOrdersBlocklistsMonitoring || 0,
            undefined,
            0,
          )}
        </div>
      ),
      paymentsOrdersMonitoringRules: (
        <div className={cn.warningReportTableRow}>
          {formatBalance(
            result.paymentsOrdersMonitoringRules || 0,
            undefined,
            0,
          )}
        </div>
      ),
      transactionsAgreements: (
        <div className={cn.warningReportTableRow}>
          {formatBalance(result.transactionsAgreements || 0, undefined, 0)}
        </div>
      ),
    };

    result.render = render;

    return result;
  });

  return nextList;
};

const CustomersWarningReport = () => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const reportControlsRef = useRef<HTMLDivElement | null>(null);

  const { ref } = useOutletContext<{
    ref: RefObject<HTMLDivElement | null>;
  }>();

  const [takeCount, setTakeCount] = useState(0);
  const [customersFilters, setFilters] = useState<{
    date?: string[];
    organizationId?: string[];
    organizationType?: string[];
    transactionTypeId?: string[];
    transactionPaymentType?: string[];
    paymentOrderType?: string[];
    actions?: string[];
  }>({
    date: undefined,
    organizationId: undefined,
    organizationType: undefined,
    transactionTypeId: undefined,
    transactionPaymentType: undefined,
    paymentOrderType: undefined,
    actions: undefined,
  });
  const [selectedOrganizations, setSelectedOrganizations] = useState<
    SelectItemProps[]
  >([]);
  const [contentHeight, setContentHeight] = useState<number>(658);

  const {
    warningsReport: {
      organizationTotalWarnings,
      organizationWarnings,
      transactionTypes,
      paymentTypes,
    },
    reportsDictionaries: { activePermissions },
  } = useAppSelector((state) => state);

  const dispatch = useAppDispatch();

  const [getList, { isFetching, isLoading }] =
    useLazyGetOrganizationsWarningsReportQuery();
  const [
    exportWarningReport,
    { isSuccess: exportIsSuccess, isLoading: exportIsLoading },
  ] = useExportWarningReportMutation();

  const { filters, onSortChange, onFilterChange } = useQueryState(schema);

  const prevFilters = usePrevious(filters);

  const { search } = useLocation();

  const { getListAction, setPage, onFilter, errorList } = useGetList<any>({
    getList,
    onFilterChange,
    onSortChange,
    schema,
  });

  const { filtersComponent, handleSubmit } = useFilters({
    filtersSchema,
    filters,
    onFilterChange: onFilter,
  });

  const {
    autocompleteList,
    onSearch,
    organizationIsLoading,
    setOrganizationSearchText,
    organizationSearchText,
    wrapperRef,
    onScroll,
    nextPageIsLoading,
  } = useGetAutocompleteList();

  const onFilterAction = (filtersValues: {
    date?: string[];
    organizationId?: string[];
    organizationType?: string[];
    transactionTypeId?: string[];
    transactionPaymentType?: string[];
    paymentOrderType?: string[];
    actions?: string[];
  }) => {
    const nextFiltersValues: {
      date?: string[];
      organizationId?: string[];
      organizationType?: string[];
      transactionTypeId?: string[];
      transactionPaymentType?: string[];
      paymentOrderType?: string[];
      actions?: string[];
      createdAtFrom?: string;
      createdAtTo?: string;
    } = { ...filtersValues };

    if (filtersValues.date?.length) {
      const [from, to] = filtersValues.date;

      nextFiltersValues.createdAtFrom = from;

      if (to) {
        nextFiltersValues.createdAtTo = to;
      }
    }

    setFilters(nextFiltersValues);

    const nextFilters: Filters = {
      take: {
        value: [takeCount?.toString()],
        type: SchemaFilterType.STRING,
      },
    };

    if (
      Object.keys(nextFiltersValues)?.filter(
        // @ts-ignore
        (item) => nextFiltersValues[item],
      )?.length
    ) {
      if (nextFiltersValues.actions) {
        nextFilters.actions = {
          value: nextFiltersValues.actions,
          type: SchemaFilterType.STRING,
        };
      }

      if (nextFiltersValues.paymentOrderType) {
        nextFilters.paymentOrderType = {
          value: nextFiltersValues.paymentOrderType,
          type: SchemaFilterType.STRING,
        };
      }

      if (nextFiltersValues.transactionPaymentType) {
        nextFilters.transactionPaymentType = {
          value: nextFiltersValues.transactionPaymentType,
          type: SchemaFilterType.STRING,
        };
      }

      if (nextFiltersValues.organizationId) {
        nextFilters.organizationId = {
          value: nextFiltersValues.organizationId,
          type: SchemaFilterType.STRING,
        };
      }

      if (nextFiltersValues.date) {
        nextFilters.createdAt = {
          value: nextFiltersValues.date,
          type: SchemaFilterType.DATE_RANGE,
        };
      }

      if (nextFiltersValues.organizationType) {
        nextFilters.organizationType = {
          value: nextFiltersValues.organizationType,
          type: SchemaFilterType.STRING,
        };
      }

      if (nextFiltersValues.transactionTypeId) {
        nextFilters.transactionTypeId = {
          value: nextFiltersValues.transactionTypeId,
          type: SchemaFilterType.STRING,
        };
      }
    }

    onFilter(nextFilters);
  };

  const onExportClick = async () => {
    const exportFilters: Record<string, string[] | string> = {};

    if (filters && Object.keys(filters)?.length) {
      Object.keys(filters)?.forEach((item) => {
        if (item !== 'createdAt') {
          if (
            item === 'organizationType' ||
            item === 'transactionTypeId' ||
            item === 'transactionPaymentType' ||
            item === 'paymentOrderType'
          ) {
            exportFilters[item] = filters[item].value;
          }
        } else if (item === 'createdAt') {
          const createdAt = filters[item];

          const [from, to] = createdAt.value;

          exportFilters.createdAtFrom = from;

          if (to) {
            exportFilters.createdAtTo = to;
          }
        }
      });
    }

    await exportWarningReport(exportFilters);
  };

  const formattedData = useMemo(() => {
    const data = formatCustomersWarnings(
      organizationWarnings?.organizations?.map((item) => item.node) as any[],
    );

    return data;
  }, [organizationWarnings]);

  useScroll({
    element: scrollRef,
    requestInProgress: isFetching,
  });

  useEffect(() => {
    if (exportIsSuccess) {
      dispatch(
        setNotificationVisible({
          visible: true,
          text: "Report in progress. You'll be notified.",
          type: 'warnings-report',
        }),
      );
    }
  }, [exportIsSuccess]);

  useEffect(() => {
    if (scrollRef && scrollRef.current) {
      const { offsetHeight } = scrollRef.current;

      if (offsetHeight) {
        const take = (offsetHeight - 51) / 46;

        setTakeCount(Math.ceil(take - 3));
      }
    }
  }, [scrollRef]);

  useEffect(() => {
    if (takeCount && takeCount !== 0 && !organizationWarnings && !search) {
      getListAction({
        filters: {
          take: {
            value: [takeCount.toString()],
            type: SchemaFilterType.STRING,
          },
        },
      });
    }
  }, [takeCount, organizationWarnings, search]);

  useEffect(() => {
    if (
      takeCount &&
      takeCount !== 0 &&
      !Object.keys(prevFilters).length &&
      !organizationWarnings &&
      search
    ) {
      const parsed = queryString.parse(search);

      const nextFilters: {
        date?: string[];
        organizationId?: string[];
        organizationType?: string[];
        transactionTypeId?: string[];
        transactionPaymentType?: string[];
        paymentOrderType?: string[];
        actions?: string[];
      } = {};

      const filtersValues: any = {
        take: {
          value: [takeCount.toString()],
          type: SchemaFilterType.STRING,
        },
      };

      if (parsed) {
        if (parsed.cursor) {
          filtersValues.cursor = {
            value: [parsed.cursor],
            type: SchemaFilterType.STRING,
          };
        }

        if (parsed?.createdAtFrom) {
          const dateFilter: string[] = [];

          dateFilter.push(parsed?.createdAtFrom as string);

          if (parsed?.createdAtTo) {
            dateFilter.push(parsed?.createdAtTo as string);
          }

          nextFilters.date = dateFilter;

          filtersValues.createdAt = {
            value: dateFilter,
            type: SchemaFilterType.DATE_RANGE,
          };
        }

        if (parsed?.organizationId) {
          nextFilters.organizationId = Array.isArray(parsed?.organizationId)
            ? (parsed?.organizationId as string[])
            : [parsed.organizationId];

          filtersValues.organizationId = {
            value: Array.isArray(parsed?.organizationId)
              ? (parsed?.organizationId as string[])
              : [parsed.organizationId],
            type: SchemaFilterType.STRING,
          };
        }

        if (parsed?.actions) {
          nextFilters.actions = Array.isArray(parsed?.actions)
            ? (parsed?.actions as string[])
            : [parsed.actions];

          filtersValues.status = {
            value: Array.isArray(parsed?.status)
              ? (parsed?.status as string[])
              : [parsed.status],
            type: SchemaFilterType.STRING,
          };
        }

        if (parsed?.organizationType) {
          nextFilters.organizationType = Array.isArray(parsed?.organizationType)
            ? (parsed?.organizationType as string[])
            : [parsed.organizationType];

          filtersValues.organizationType = {
            value: Array.isArray(parsed?.organizationType)
              ? (parsed?.organizationType as string[])
              : [parsed.organizationType],
            type: SchemaFilterType.STRING,
          };
        }

        if (parsed?.transactionTypeId) {
          nextFilters.transactionTypeId = Array.isArray(
            parsed?.transactionTypeId,
          )
            ? (parsed?.transactionTypeId as string[])
            : [parsed.transactionTypeId];

          filtersValues.transactionTypeId = {
            value: Array.isArray(parsed?.transactionTypeId)
              ? (parsed?.transactionTypeId as string[])
              : [parsed.transactionTypeId],
            type: SchemaFilterType.STRING,
          };
        }

        if (parsed?.transactionPaymentType) {
          nextFilters.transactionPaymentType = Array.isArray(
            parsed?.transactionPaymentType,
          )
            ? (parsed?.transactionPaymentType as string[])
            : [parsed.transactionPaymentType];

          filtersValues.transactionPaymentType = {
            value: Array.isArray(parsed?.transactionPaymentType)
              ? (parsed?.transactionPaymentType as string[])
              : [parsed.transactionPaymentType],
            type: SchemaFilterType.STRING,
          };
        }

        if (parsed?.paymentOrderType) {
          nextFilters.paymentOrderType = Array.isArray(parsed?.paymentOrderType)
            ? (parsed?.paymentOrderType as string[])
            : [parsed.paymentOrderType];

          filtersValues.paymentOrderType = {
            value: Array.isArray(parsed?.paymentOrderType)
              ? (parsed?.paymentOrderType as string[])
              : [parsed.paymentOrderType],
            type: SchemaFilterType.STRING,
          };
        }

        if (parsed.organizationId) {
          const organizationsList: SelectItemProps[] = [];

          if (autocompleteList) {
            autocompleteList?.forEach((item) => {
              if (
                (nextFilters?.organizationId as string[])?.find(
                  (id) => item.value === id,
                )
              ) {
                organizationsList.push(item);
              }
            });

            if (Array.isArray(parsed.organizationId)) {
              parsed.organizationId?.forEach((item) => {
                if (
                  !organizationsList.find(
                    (organization) => organization.value === item,
                  )
                ) {
                  organizationsList.push({
                    name: '',
                    value: item as string,
                  });
                }
              });
            } else {
              // eslint-disable-next-line no-lonely-if
              if (
                !organizationsList.find(
                  (organization) =>
                    organization.value === parsed.organizationId,
                )
              ) {
                organizationsList.push({
                  name: '',
                  value: parsed.organizationId,
                });
              }
            }
          }

          setSelectedOrganizations(organizationsList);
        }

        setFilters(nextFilters);

        getListAction({
          filters: filtersValues,
        });
      }
    }
  }, [organizationWarnings, takeCount, search, organizationWarnings]);

  useEffect(() => {
    if (ref && ref.current && reportControlsRef && reportControlsRef.current) {
      const refHeight = ref.current.offsetHeight;
      const reportControlsRefHeight = reportControlsRef?.current?.offsetHeight;

      if (refHeight && reportControlsRefHeight) {
        const wrapperHeight = refHeight - reportControlsRefHeight;

        setContentHeight(wrapperHeight);
      }
    }
  }, [ref]);

  let content;

  if (isLoading) {
    content = (
      <div className={cn.warningCustomersReportsLoader}>
        <IconLoader />
      </div>
    );
  } else {
    content = (
      <>
        <div ref={reportControlsRef}>
          <div className={cn.warningsReportOverviewControls}>
            <WarningsReportFilters
              filters={customersFilters}
              onFilter={onFilterAction}
              onSearch={onSearch}
              autocompleteList={autocompleteList}
              selectedOrganizations={selectedOrganizations}
              organizationIsLoading={organizationIsLoading}
              organizationSearchText={organizationSearchText}
              setOrganizationSearchText={setOrganizationSearchText}
              wrapperRef={wrapperRef}
              onScroll={onScroll}
              nextPageIsLoading={nextPageIsLoading}
              transactionTypes={transactionTypes}
              paymentTypes={paymentTypes}
              customersFilters
            />
          </div>
          <div className={cn.warningsCustomersReportsTotalWrapper}>
            <div className={cn.warningsCustomersReportsTotal}>
              {formatBalance(
                (organizationTotalWarnings as number) || 0,
                undefined,
                0,
              )}
              &nbsp;
              <span>Total warnings</span>
            </div>
            {activePermissions?.includes(
              PermissionEnum.WARNINGS_DOWNLOAD_ACTION,
            ) && (
              <ButtonSecondary
                text={exportIsLoading ? 'Loading...' : 'Download Report'}
                icon={<IconDownload />}
                className="button-white"
                size={ButtonSize.SMALL}
                onClick={onExportClick}
              />
            )}
          </div>
        </div>
        <div
          style={{ height: `${contentHeight}px` }}
          className={cn.customersReportTableWrapper}
        >
          <Table
            columns={columns}
            data={formattedData || []}
            sort={{}}
            onSort={() => {}}
            filters={filters}
            error={errorList}
            columnsLabels={columnsLabels}
            tableFormClassName={cn.customersListForm}
            onFilterChange={() => {}}
            filtersComponent={filtersComponent}
            handleSubmit={handleSubmit}
            isLoading={isFetching}
            take={takeCount?.toString()}
            setPage={(data) => {
              const filterValues: Filters = {
                ...filters,
                take: {
                  value: [takeCount?.toString()],
                  type: SchemaFilterType.STRING,
                },
              };

              setPage({ ...data, filters: filterValues });
            }}
            pagination={organizationWarnings?.pageInfo}
            clickable
          />
        </div>
      </>
    );
  }

  return (
    <div className={cn.warningCustomersReports} ref={scrollRef}>
      {content}
      <Outlet />
    </div>
  );
};

export default CustomersWarningReport;
